import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const BeatsCartContext = createContext(null);

export const useBeatsCart = () => {
  const context = useContext(BeatsCartContext);
  if (!context) {
    throw new Error('useBeatsCart must be used within a BeatsCartProvider');
  }
  return context;
};

export const BeatsCartProvider = ({ children }) => {
  const [items, setItems] = useState([]); // {beat, licenseType, price}
  const [isOpen, setIsOpen] = useState(false);

  const addBeat = useCallback((beat, licenseType) => {
    setItems(prev => {
      // Don't add duplicates of same beat + license combo
      if (prev.find(item => item.beat.id === beat.id && item.licenseType === licenseType)) {
        return prev;
      }
      
      // Determine price based on license type
      const priceMap = {
        'basic_lease': beat.basic_lease_price || 29.99,
        'premium_lease': beat.premium_lease_price || 99.99,
        'exclusive_rights': beat.exclusive_rights_price || 299.99
      };
      
      return [...prev, {
        beat,
        licenseType,
        price: priceMap[licenseType] || 29.99
      }];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((beatId, licenseType) => {
    setItems(prev => prev.filter(item => 
      !(item.beat.id === beatId && item.licenseType === licenseType)
    ));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const toggleCart = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const closeCart = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openCart = useCallback(() => {
    setIsOpen(true);
  }, []);

  const isInCart = useCallback((beatId, licenseType) => {
    return items.some(item => item.beat.id === beatId && item.licenseType === licenseType);
  }, [items]);

  const total = useMemo(() => {
    return items.reduce((sum, item) => sum + item.price, 0);
  }, [items]);

  const itemCount = items.length;

  // Split memoization: cart state vs cart actions
  const cartState = React.useMemo(() => ({
    items,
    isOpen,
    total,
    itemCount,
  }), [items, isOpen, total, itemCount]);

  const cartActions = React.useMemo(() => ({
    addBeat,
    removeItem,
    clearCart,
    toggleCart,
    closeCart,
    openCart,
    isInCart
  }), [addBeat, removeItem, clearCart, toggleCart, closeCart, openCart, isInCart]);

  const value = React.useMemo(() => ({
    ...cartState,
    ...cartActions
  }), [cartState, cartActions]);

  return (
    <BeatsCartContext.Provider value={value}>
      {children}
    </BeatsCartContext.Provider>
  );
};
